'use client';

import { useEffect, useRef, useState } from 'react';
import { HANDWRITING_FONT_STACK } from '@/lib/browser-colortext-tools';

const CANVAS_WIDTH = 720;
const PADDING_X = 48;
const PADDING_TOP = 56;

export default function BrowserTextToHandwriting() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [text, setText] = useState(
    'Dear friend,\nThis note was typed but it reads like handwriting.\nEvery letter wobbles just a little.',
  );
  const [inkColor, setInkColor] = useState('#1d4ed8');
  const [fontSize, setFontSize] = useState(28);
  const [lineHeight, setLineHeight] = useState(46);
  const [ruledPaper, setRuledPaper] = useState(true);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    const lines = wrapLines(context, text, fontSize, CANVAS_WIDTH - PADDING_X * 2);
    const height = Math.max(240, PADDING_TOP * 2 + lines.length * lineHeight);
    canvas.width = CANVAS_WIDTH;
    canvas.height = height;

    // Paper background.
    context.fillStyle = '#fffef7';
    context.fillRect(0, 0, canvas.width, canvas.height);

    if (ruledPaper) {
      context.strokeStyle = 'rgba(59, 130, 246, 0.25)';
      context.lineWidth = 1;
      for (let y = PADDING_TOP; y < canvas.height - 10; y += lineHeight) {
        context.beginPath();
        context.moveTo(PADDING_X / 2, y + fontSize * 0.35);
        context.lineTo(canvas.width - PADDING_X / 2, y + fontSize * 0.35);
        context.stroke();
      }
      // Margin line.
      context.strokeStyle = 'rgba(239, 68, 68, 0.4)';
      context.beginPath();
      context.moveTo(PADDING_X - 6, 0);
      context.lineTo(PADDING_X - 6, canvas.height);
      context.stroke();
    }

    context.fillStyle = inkColor;
    context.textBaseline = 'alphabetic';

    lines.forEach((line, lineIndex) => {
      const baseY = PADDING_TOP + lineIndex * lineHeight + fontSize;
      let cursorX = PADDING_X;
      let charIndex = 0;
      for (const char of line) {
        // Seeded on the glyph's position, so the same text always renders the same way.
        // Math.random() here re-jittered every glyph on every keystroke.
        const seed = lineIndex * 4096 + charIndex;
        charIndex += 1;
        const noise = (salt: number) => {
          const value = Math.sin((seed + 1) * 12.9898 + salt * 78.233) * 43758.5453;
          return value - Math.floor(value) - 0.5;
        };
        const jitterY = noise(1) * fontSize * 0.14;
        const jitterSize = 1 + noise(2) * 0.12;
        const rotation = noise(3) * 0.08;
        const glyphSize = fontSize * jitterSize;
        context.font = `${glyphSize}px ${HANDWRITING_FONT_STACK}`;

        context.save();
        context.translate(cursorX, baseY + jitterY);
        context.rotate(rotation);
        context.fillText(char, 0, 0);
        context.restore();

        cursorX += context.measureText(char).width;
      }
    });
  }, [text, inkColor, fontSize, lineHeight, ruledPaper]);

  function handleDownload() {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = 'handwriting.png';
    document.body.append(link);
    link.click();
    link.remove();
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Text</span>
        <h2 className="tool-live-title">Text to handwriting</h2>
        <p className="tool-live-subtitle">
          Turn typed text into a handwriting-style note on ruled paper with adjustable ink, size,
          and spacing, then download it as a PNG.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Your text</span>
              <textarea onChange={(event) => setText(event.target.value)} rows={6} value={text} />
            </label>
            <label className="tool-live-field">
              <span>Ink color</span>
              <input
                onChange={(event) => setInkColor(event.target.value)}
                type="color"
                value={inkColor}
              />
            </label>
            <label className="tool-live-field">
              <span>Font size: {fontSize}px</span>
              <input
                max={48}
                min={16}
                onChange={(event) => setFontSize(Number(event.target.value))}
                type="range"
                value={fontSize}
              />
            </label>
            <label className="tool-live-field">
              <span>Line height: {lineHeight}px</span>
              <input
                max={72}
                min={28}
                onChange={(event) => setLineHeight(Number(event.target.value))}
                type="range"
                value={lineHeight}
              />
            </label>
            <label
              className="tool-live-field"
              style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}
            >
              <input
                checked={ruledPaper}
                onChange={(event) => setRuledPaper(event.target.checked)}
                type="checkbox"
              />
              <span>Ruled paper</span>
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleDownload} type="button">
              Download PNG
            </button>
          </div>
          <p className="tool-live-panel-note">Rendered locally on a canvas, nothing is uploaded.</p>
        </section>

        <section className="tool-live-preview">
          <canvas
            aria-label="Handwriting preview"
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '0.5rem',
              border: '1px solid rgba(148, 163, 184, 0.35)',
            }}
          />
        </section>
      </div>
    </div>
  );
}

// Word-wrap the text to the drawable width, preserving explicit newlines.
function wrapLines(
  context: CanvasRenderingContext2D,
  text: string,
  fontSize: number,
  maxWidth: number,
): string[] {
  context.font = `${fontSize}px ${HANDWRITING_FONT_STACK}`;
  const lines: string[] = [];

  for (const paragraph of text.split('\n')) {
    if (paragraph.length === 0) {
      lines.push('');
      continue;
    }
    const words = paragraph.split(' ');
    let current = '';
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (context.measureText(candidate).width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) {
      lines.push(current);
    }
  }

  return lines.length > 0 ? lines : [''];
}
